import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  IconButton,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  BugReport as BugIcon,
  Storage as StorageIcon,
  Api as ApiIcon,
  Computer as ComputerIcon
} from '@mui/icons-material';
import { api } from '../../services/api';

const DataIntegrityPanel = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [expandedChecks, setExpandedChecks] = useState({});
  const [lastRun, setLastRun] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [exportLoading, setExportLoading] = useState(false);
  const [checkProgress, setCheckProgress] = useState({});
  const [systemHealth, setSystemHealth] = useState(null);

  const integrityChecks = [
    {
      id: 'saving_calculation',
      name: 'Saving Calculation Consistency',
      description: 'Check if saving calculations are consistent between frontend, backend, and API',
      category: 'Financial Data',
      severity: 'high'
    },
    {
      id: 'campaign_totals',
      name: 'Campaign Totals Accuracy',
      description: 'Verify campaign totals match sum of monthly data',
      category: 'Aggregation',
      severity: 'high'
    },
    {
      id: 'media_type_aggregation',
      name: 'Media Type Aggregation',
      description: 'Check media type totals match sum of monthly data',
      category: 'Aggregation',
      severity: 'medium'
    },
    {
      id: 'api_response_consistency',
      name: 'API Response Consistency',
      description: 'Verify API responses are consistent across different endpoints',
      category: 'API',
      severity: 'medium'
    },
    {
      id: 'frontend_backend_sync',
      name: 'Frontend-Backend Sync',
      description: 'Check if frontend displays match backend calculations',
      category: 'Data Sync',
      severity: 'high'
    },
    {
      id: 'monthly_data_integrity',
      name: 'Monthly Data Integrity',
      description: 'Verify monthly data calculations and relationships',
      category: 'Data Integrity',
      severity: 'high'
    },
    {
      id: 'status_consistency',
      name: 'Status Consistency',
      description: 'Check if campaign status values are consistent between frontend and backend',
      category: 'Data Sync',
      severity: 'medium'
    },
    {
      id: 'actual_cost_calculation',
      name: 'Actual Cost Calculation',
      description: 'Verify actual_gross_cost is calculated correctly from components',
      category: 'Financial Data',
      severity: 'high'
    },
    {
      id: 'n2_logic_consistency',
      name: 'N-2 Logic Consistency',
      description: 'Check if n-2 logic is consistently applied for campaign status',
      category: 'Business Logic',
      severity: 'high'
    },
    {
      id: 'null_handling_consistency',
      name: 'Null Handling Consistency',
      description: 'Check if null handling is consistent between frontend and backend',
      category: 'Data Integrity',
      severity: 'medium'
    }
  ];

  const runIntegrityCheck = async () => {
    setLoading(true);
    setResults(null);
    
    try {
      const response = await api.post('/integrity/check/', {
        checks: integrityChecks.map(check => check.id)
      });
      
      setResults(response.data);
      setLastRun(new Date());
    } catch (error) {
      console.error('Integrity check failed:', error);
      
      // Enhanced error handling with specific messages
      let errorMessage = 'Failed to run integrity check';
      
      if (error.response) {
        switch (error.response.status) {
          case 401:
            errorMessage = 'Authentication required. Please log in again.';
            break;
          case 403:
            errorMessage = 'Access denied. Admin privileges required.';
            break;
          case 404:
            errorMessage = 'Integrity check endpoint not found.';
            break;
          case 500:
            errorMessage = 'Server error occurred during integrity check.';
            break;
          default:
            errorMessage = error.response.data?.detail || error.response.data?.error || errorMessage;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      }
      
      setResults({
        success: false,
        error: errorMessage,
        checks: []
      });
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'passed': return <CheckIcon color="success" />;
      case 'failed': return <ErrorIcon color="error" />;
      case 'warning': return <WarningIcon color="warning" />;
      default: return <WarningIcon color="disabled" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'passed': return 'success';
      case 'failed': return 'error';
      case 'warning': return 'warning';
      default: return 'default';
    }
  };

  const toggleExpanded = (checkId) => {
    setExpandedChecks(prev => ({
      ...prev,
      [checkId]: !prev[checkId]
    }));
  };

  const retryIntegrityCheck = async () => {
    setRetryCount(prev => prev + 1);
    await runIntegrityCheck();
  };

  const handleExportReport = async () => {
    setExportLoading(true);
    try {
      const response = await api.get('/integrity/export/', {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `integrity_report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      // Could add a toast notification here
    } finally {
      setExportLoading(false);
    }
  };

  const runIndividualCheck = async (checkId) => {
    setCheckProgress(prev => ({ ...prev, [checkId]: true }));
    
    try {
      const response = await api.post('/integrity/check/', {
        checks: [checkId]
      });
      
      // Update results with individual check result
      if (response.data && response.data.checks && response.data.checks.length > 0) {
        const newCheckResult = response.data.checks[0];
        setResults(prev => {
          if (!prev) return response.data;
          
          const updatedChecks = prev.checks.map(check => 
            check.id === checkId ? newCheckResult : check
          );
          
          return {
            ...prev,
            checks: updatedChecks
          };
        });
      }
    } catch (error) {
      console.error(`Individual check failed for ${checkId}:`, error);
    } finally {
      setCheckProgress(prev => ({ ...prev, [checkId]: false }));
    }
  };

  const fetchSystemHealth = async () => {
    try {
      const response = await api.get('/integrity/health/');
      setSystemHealth(response.data);
    } catch (error) {
      console.error('Failed to fetch system health:', error);
    }
  };

  // Fetch system health on component mount
  useEffect(() => {
    fetchSystemHealth();
  }, []);

  const renderCheckDetails = (check) => {
    if (!results?.checks) return null;
    
    const checkResult = results.checks.find(c => c.id === check.id);
    if (!checkResult) return null;

    return (
      <AccordionDetails>
        <Box sx={{ mt: 2 }}>
          {checkResult.details && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Details:
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                {checkResult.details}
              </Typography>
            </Box>
          )}
          
          {checkResult.discrepancies && checkResult.discrepancies.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="error" gutterBottom>
                Discrepancies Found:
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Field</TableCell>
                      <TableCell>Expected</TableCell>
                      <TableCell>Actual</TableCell>
                      <TableCell>Difference</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {checkResult.discrepancies.map((disc, index) => (
                      <TableRow key={index}>
                        <TableCell>{disc.field}</TableCell>
                        <TableCell>{disc.expected}</TableCell>
                        <TableCell>{disc.actual}</TableCell>
                        <TableCell>
                          <Chip 
                            label={disc.difference} 
                            color={Math.abs(disc.difference) > 0.01 ? 'error' : 'success'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          
          {checkResult.recommendations && checkResult.recommendations.length > 0 && (
            <Box>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Recommendations:
              </Typography>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                {checkResult.recommendations.map((rec, index) => (
                  <Typography key={index} component="li" variant="body2" sx={{ mb: 0.5 }}>
                    {rec}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </AccordionDetails>
    );
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Data Integrity & Consistency Checker
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Verify data consistency between frontend, backend, and API endpoints
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <BugIcon />}
            onClick={runIntegrityCheck}
            disabled={loading}
            sx={{ minWidth: 150 }}
          >
            {loading ? 'Running...' : 'Run Checks'}
          </Button>
          
          {results && !results.success && (
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={retryIntegrityCheck}
              disabled={loading}
              sx={{ minWidth: 120 }}
            >
              Retry ({retryCount})
            </Button>
          )}
        </Box>
      </Box>

      {lastRun && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Last run: {lastRun.toLocaleString()}
        </Alert>
      )}

      {systemHealth && (
        <Alert 
          severity={systemHealth.system_health?.overall === 'healthy' ? 'success' : 'warning'} 
          sx={{ mb: 3 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2">
              System Health: <strong>{systemHealth.system_health?.overall || 'Unknown'}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Database: {systemHealth.system_health?.database || 'Unknown'} | 
              Cache: {systemHealth.system_health?.cache || 'Unknown'}
            </Typography>
            {systemHealth.model_counts && (
              <Typography variant="body2" color="text.secondary">
                | Records: {systemHealth.model_counts.monthly_data || 0} monthly, {systemHealth.model_counts.campaigns || 0} campaigns
              </Typography>
            )}
          </Box>
        </Alert>
      )}

      {results?.error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {results.error}
        </Alert>
      )}

      {results?.success && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Summary
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Chip 
                    icon={<CheckIcon />}
                    label={`${results.checks.filter(c => c.status === 'passed').length} Passed`}
                    color="success"
                    variant="outlined"
                  />
                  <Chip 
                    icon={<ErrorIcon />}
                    label={`${results.checks.filter(c => c.status === 'failed').length} Failed`}
                    color="error"
                    variant="outlined"
                  />
                  <Chip 
                    icon={<WarningIcon />}
                    label={`${results.checks.filter(c => c.status === 'warning').length} Warnings`}
                    color="warning"
                    variant="outlined"
                  />
                </Box>
                
                {results.overall_score && (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h4" color="primary">
                      {results.overall_score}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Overall Integrity Score
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quick Actions
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={exportLoading ? <CircularProgress size={16} /> : <StorageIcon />}
                    size="small"
                    onClick={handleExportReport}
                    disabled={exportLoading || !results?.success}
                  >
                    {exportLoading ? 'Exporting...' : 'Export Report'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ApiIcon />}
                    size="small"
                    onClick={() => window.open('/integrity/logs/', '_blank')}
                  >
                    View Logs
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ComputerIcon />}
                    size="small"
                    onClick={fetchSystemHealth}
                  >
                    Refresh Health
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Integrity Checks
        </Typography>
        
        {integrityChecks.map((check) => {
          const checkResult = results?.checks?.find(c => c.id === check.id);
          const isExpanded = expandedChecks[check.id];
          
          return (
            <Accordion 
              key={check.id}
              expanded={isExpanded}
              onChange={() => toggleExpanded(check.id)}
              sx={{ mb: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  {checkResult ? getStatusIcon(checkResult.status) : <WarningIcon color="disabled" />}
                  
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      {check.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {check.description}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip 
                      label={check.category} 
                      size="small" 
                      variant="outlined"
                      color="primary"
                    />
                    <Chip 
                      label={check.severity} 
                      size="small"
                      color={getSeverityColor(check.severity)}
                    />
                    {checkResult && (
                      <Chip 
                        label={checkResult.status} 
                        size="small"
                        color={getStatusColor(checkResult.status)}
                        variant="filled"
                      />
                    )}
                    <Tooltip title="Run individual check">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          runIndividualCheck(check.id);
                        }}
                        disabled={checkProgress[check.id]}
                        sx={{ ml: 1 }}
                      >
                        {checkProgress[check.id] ? (
                          <CircularProgress size={16} />
                        ) : (
                          <RefreshIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </AccordionSummary>
              
              {renderCheckDetails(check)}
            </Accordion>
          );
        })}
      </Box>
    </Box>
  );
};

export default DataIntegrityPanel;
